import * as React from 'react'

import type { IconNode, LucideIcon, LucideProps } from 'lucide-react'
import { Icon } from 'lucide-react'
import { observer } from 'mobx-react'

import { createShardedArt } from '../shardedArt'
import type { PictogramSlug } from './pictogramCatalog'
import { PICTOGRAM_GRID, PICTOGRAMS } from './pictogramCatalog'
import {
  NEUTRAL_TONE,
  PICTOGRAM_SHARD_LOADERS,
  PICTOGRAM_SHARDS,
  PICTOGRAM_TILE_PATH,
  PICTOGRAM_TONES,
  PLACEHOLDER_NODES,
} from './pictogramIndex.generated'

// Sign art loads by shard on first read; until a sign's art lands it is drawn as its tile in its
// own colour.
const art = createShardedArt({
  names: PICTOGRAMS.map(({ slug }) => slug),
  shardOf: slug => PICTOGRAM_SHARDS[slug],
  loaders: PICTOGRAM_SHARD_LOADERS,
  missing: PLACEHOLDER_NODES,
})

const toneTiles = new Map<PictogramSlug, IconNode>()
const toneTile = (slug: PictogramSlug): IconNode => {
  let tile = toneTiles.get(slug)
  if (!tile) {
    tile = [
      [
        'path',
        {
          key: '0',
          d: PICTOGRAM_TILE_PATH,
          fill: PICTOGRAM_TONES[slug] ?? NEUTRAL_TONE,
          stroke: 'none',
        },
      ],
    ]
    toneTiles.set(slug, tile)
  }
  return tile
}

/** A sign's art, or its tone tile while the art loads. */
export const pictogramNodes = (slug: PictogramSlug): IconNode =>
  art.read(slug) ?? toneTile(slug)

export const isPictogramArtLoaded = art.isLoaded

export const hasPendingPictogramArt = art.hasPending

/** Settles once every load already started has, so a capture never bakes in a tone tile. */
export const pictogramArtSettled = art.settled

// The bare tile, edge to edge: for surfaces where the art is the whole object (marker, picker tile).
export const PICTOGRAM_VIEW_BOX = `0 0 ${PICTOGRAM_GRID} ${PICTOGRAM_GRID}`

// As an icon, the tile fills Lucide's live area — the 24 grid less its 2px padding, which is
// where a square glyph's stroked edge lands (square-parking) — so a pictogram listed beside a
// StruxHub icon at the same size reads the same size.
const LUCIDE_GRID = 24
const LUCIDE_PADDING = 2
const GRID_UNIT = PICTOGRAM_GRID / (LUCIDE_GRID - LUCIDE_PADDING * 2)
const GLYPH_ORIGIN = -LUCIDE_PADDING * GRID_UNIT
const GLYPH_SIZE = LUCIDE_GRID * GRID_UNIT
const PICTOGRAM_GLYPH_VIEW_BOX = `${GLYPH_ORIGIN} ${GLYPH_ORIGIN} ${GLYPH_SIZE} ${GLYPH_SIZE}`

/**
 * Whether signs drawn inside may load their art. A long list closes it for what is out of view,
 * so opening it fetches only the shards it shows; those signs are drawn as their tone tile.
 */
export const PictogramArtGate = React.createContext(true)

const pictogramIcon = (slug: PictogramSlug): LucideIcon =>
  observer(
    React.forwardRef<SVGSVGElement, LucideProps>((props, ref) =>
      React.createElement(Icon, {
        icon: {
          name: `pictogram-${slug}`,
          size: PICTOGRAM_GRID,
          node: React.useContext(PictogramArtGate)
            ? pictogramNodes(slug)
            : (art.peek(slug) ?? toneTile(slug)),
        },
        viewBox: PICTOGRAM_GLYPH_VIEW_BOX,
        ...props,
        ref,
      }),
    ),
  ) as LucideIcon

export const PICTOGRAM_ICONS = Object.fromEntries(
  PICTOGRAMS.map(({ slug }) => [slug, pictogramIcon(slug)]),
) as Record<PictogramSlug, LucideIcon>
